import { useEffect, useState } from 'react';
import { getV2AiStatus } from '../../../api/studioV2.api';

export const GEMINI_MODELS = ['gemini-3.5-flash-lite', 'gemini-3.5-flash', 'gemini-2.5-flash', 'gemini-2.5-pro'];
export const AI_STEP_TYPES = new Set(['llm', 'ai-agent']);

const PROVIDER_LABELS = {
  ollama: 'Local model',
  gemini: 'Google Gemini',
};

export function providerLabel(provider) {
  return PROVIDER_LABELS[provider] || provider || 'Model';
}

/** Plain note for an answer that came from Gemini because the local model failed or was slow. */
export function fallbackNote(fallback) {
  return fallback?.to ? `Answered by ${fallback.to} because ${fallback.reason}.` : '';
}

let cached = null;
let inflight = null;

export function fetchAiStatus({ refresh = false } = {}) {
  if (cached && !refresh) return Promise.resolve(cached);
  if (!inflight) {
    inflight = getV2AiStatus().then(({ status }) => {
      inflight = null;
      if (status) cached = status;
      return status;
    });
  }
  return inflight;
}

/** Shared, cached view of `GET /api/v2/ai/status`; `null` until loaded or when unavailable. */
export function useAiStatus() {
  const [status, setStatus] = useState(cached);
  useEffect(() => {
    let alive = true;
    fetchAiStatus().then((s) => { if (alive && s) setStatus(s); });
    return () => { alive = false; };
  }, []);
  return status;
}

export function resetAiStatusCacheForTests() {
  cached = null;
  inflight = null;
}

/**
 * What an AI step will actually run on. Steps without provider/model use the workspace default;
 * a model without a provider goes to the default provider (the runtime does the same).
 */
export function resolveStepModel(cfg, status) {
  const c = cfg && typeof cfg === 'object' ? cfg : {};
  const provider = String(c.provider || '').trim();
  const model = String(c.model || '').trim();
  const defaultProvider = status?.default?.provider || 'ollama';
  if (!provider && !model) {
    return { isDefault: true, provider: defaultProvider, model: status?.default?.model || '' };
  }
  const p = provider || defaultProvider;
  let m = model;
  if (!m) m = p === 'gemini' ? (status?.providers?.gemini?.defaultModel || GEMINI_MODELS[0]) : (status?.default?.model || '');
  return { isDefault: false, provider: p, model: m };
}

/** Why a resolved model cannot run, in plain words, or '' when it can (or status is unknown). */
export function modelProblem(resolved, status) {
  if (!status || !resolved) return '';
  if (resolved.provider === 'gemini' && !status.providers?.gemini?.ready) {
    return 'Google Gemini is not set up for this workspace, so this step will fail.';
  }
  if (resolved.provider === 'ollama' && !status.providers?.gemini?.ready) {
    const local = status.providers?.ollama;
    if (!local?.ready) return 'The local model server is not responding, so this step will fail.';
    if (resolved.model && local.models?.length && !local.models.includes(resolved.model)) {
      return `The local model "${resolved.model}" is not installed on this server.`;
    }
  }
  if (!PROVIDER_LABELS[resolved.provider]) {
    return `"${resolved.provider}" is not a model provider this workspace supports.`;
  }
  return '';
}

/** AI steps in a flow definition that would fail because their model is unavailable. */
export function aiStepProblems(def, status) {
  if (!status || !Array.isArray(def?.nodes)) return [];
  return def.nodes
    .filter((n) => AI_STEP_TYPES.has(String(n?.type || '')))
    .map((n) => {
      const resolved = resolveStepModel(n.config, status);
      return {
        id: n.id, label: n.label || n.id, resolved, message: modelProblem(resolved, status),
      };
    })
    .filter((p) => p.message);
}

export function usesLocalModel(def, status) {
  if (!Array.isArray(def?.nodes)) return false;
  return def.nodes.some((n) => AI_STEP_TYPES.has(String(n?.type || ''))
    && resolveStepModel(n.config, status).provider === 'ollama');
}
